/* Shared behaviours for Only Snacks sections. One file, re-initialised on
   shopify:section:load so everything keeps working inside the theme editor.
   Contracts:
     [data-os-tabs]      > [data-os-tab="i"] buttons + [data-os-panel="i"] panels;
                           a tab may carry data-os-accent="<colour>" and the group
                           takes it as --os-accent when that tab is active
     [data-os-scroller]  > [data-os-prev] [data-os-next] [data-os-track]
     [data-os-thumbs]    > [data-os-thumb="url"] buttons + [data-os-stage] img
     details[data-os-collapse] - open on desktop/tablet, an accordion on mobile
                           (closed unless it also carries data-os-open)
     [data-os-card][data-variant] > [data-os-card-add] button, [data-os-card-stepper]
                           with [data-os-card-step="±1"] and [data-os-card-qty]  -
                           add-to-cart card that turns into a quantity stepper
     [data-os-video]     > video + optional [data-os-play] button - review video
                           that plays one at a time (is-playing while it runs)
   Cart cards need window.OSCart (os-cart.js loads first; os:cart-ready covers
   the case where it does not). */
(() => {
  // Marks an element as initialised; true means it was already claimed (skip it).
  const alreadyClaimed = (el, key) => {
    if (el.dataset[key]) return true;
    el.dataset[key] = '1';
    return false;
  };

  const initTabs = (root) => root.querySelectorAll('[data-os-tabs]').forEach((box) => {
    if (alreadyClaimed(box, 'osTabsReady')) return;
    const tabs = [...box.querySelectorAll('[data-os-tab]')];
    const panels = [...box.querySelectorAll('[data-os-panel]')];
    const activeTab = tabs.find((btn) => btn.getAttribute('aria-selected') === 'true') || tabs[0];
    tabs.forEach((btn, index) => {
      btn.tabIndex = btn === activeTab ? 0 : -1;
      btn.addEventListener('keydown', (event) => {
        const vertical = btn.closest('[role="tablist"]')?.getAttribute('aria-orientation') === 'vertical';
        let next;
        if (event.key === (vertical ? 'ArrowDown' : 'ArrowRight')) next = (index + 1) % tabs.length;
        else if (event.key === (vertical ? 'ArrowUp' : 'ArrowLeft')) next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        tabs[next].click();
        tabs[next].focus();
      });
    });
    tabs.forEach((btn) => btn.addEventListener('click', () => {
      const i = btn.dataset.osTab;
      tabs.forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
      });
      panels.forEach((p) => {
        const hide = p.dataset.osPanel !== i;
        p.classList.toggle('is-hidden', hide);
        // A video playing in a panel that just got hidden stops.
        if (hide) p.querySelectorAll('video').forEach((v) => v.pause());
      });
      // A tab may carry an accent colour (flavour explorer): the group recolours to it.
      if (btn.dataset.osAccent) box.style.setProperty('--os-accent', btn.dataset.osAccent);
      document.dispatchEvent(new Event('os:tab-change'));
    }));
  });

  // A track whose cards snap to the centre opens with its first card centred,
  // whatever the browser decided about the initial snap position.
  const centreFirst = (track) => {
    const first = track && (track.closest('[data-os-review-loop]') && window.matchMedia('(max-width: 640px)').matches ? track.children[1] : track.firstElementChild);
    if (!first || !getComputedStyle(first).scrollSnapAlign.includes('center')) return;
    if (track.scrollLeft > 0) return; // the visitor has moved on; never yank them back
    const left = first.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    const target = Math.max(0, left + first.offsetWidth / 2 - track.clientWidth / 2);
    if (Math.abs(track.scrollLeft - target) > 1) track.scrollLeft = target;
  };
  const initScrollers = (root) => root.querySelectorAll('[data-os-scroller]').forEach((box) => {
    if (alreadyClaimed(box, 'osScrollerReady')) return;
    const track = box.querySelector('[data-os-track]');
    if (!track) return;
    centreFirst(track);
    if (box.hasAttribute('data-os-review-loop')) {
      track.addEventListener('scrollend', () => {
        if (!window.matchMedia('(max-width: 640px)').matches) return;
        const cards = [...track.children];
        const center = track.getBoundingClientRect().left + track.clientWidth / 2;
        const atCenter = (card) => Math.abs(card.getBoundingClientRect().left + card.offsetWidth / 2 - center) < 2;
        const target = atCenter(cards[0]) ? cards[cards.length - 2] : atCenter(cards[cards.length - 1]) ? cards[1] : null;
        if (target) track.scrollTo({ left: track.scrollLeft + target.getBoundingClientRect().left + target.offsetWidth / 2 - center, behavior: 'instant' });
      });
    }
    // One card per press, measured from the real gap; the arrows wrap around
    // so the strip never runs off into empty space.
    const cards = () => [...track.children];
    const step = () => {
      const [a, b] = cards();
      return a && b ? b.getBoundingClientRect().left - a.getBoundingClientRect().left : (a?.offsetWidth || 280) + 24;
    };
    const maxLeft = () => track.scrollWidth - track.clientWidth;
    const go = (dir) => {
      const at = track.scrollLeft;
      if (dir > 0 && at >= maxLeft() - 2) { track.scrollTo({ left: 0, behavior: 'smooth' }); return; }
      if (dir < 0 && at <= 2) { track.scrollTo({ left: maxLeft(), behavior: 'smooth' }); return; }
      track.scrollBy({ left: dir * step(), behavior: 'smooth' });
    };
    box.querySelector('[data-os-prev]')?.addEventListener('click', () => go(-1));
    box.querySelector('[data-os-next]')?.addEventListener('click', () => go(1));
  });

  // Gallery: thumbs swap the stage image; optional [data-os-prev]/[data-os-next]
  // arrows page through them (wrapping). One image or fewer hides the arrows.
  const initThumbs = (root) => root.querySelectorAll('[data-os-thumbs]').forEach((box) => {
    if (alreadyClaimed(box, 'osThumbsReady')) return;
    const stage = box.querySelector('[data-os-stage]'); if (!stage) return;
    const thumbs = [...box.querySelectorAll('[data-os-thumb]')];
    if (thumbs.length < 2) box.dataset.osSingle = '1';
    const show = (index) => {
      const i = (index + thumbs.length) % thumbs.length;
      stage.src = thumbs[i].dataset.osThumb;
      thumbs.forEach((b, j) => b.classList.toggle('is-active', j === i));
    };
    const current = () => Math.max(0, thumbs.findIndex((b) => b.classList.contains('is-active')));
    thumbs.forEach((btn, i) => btn.addEventListener('click', () => show(i)));
    box.querySelector('[data-os-prev]')?.addEventListener('click', () => show(current() - 1));
    box.querySelector('[data-os-next]')?.addEventListener('click', () => show(current() + 1));
  });

  const mobile = window.matchMedia('(max-width: 640px)');
  const applyCollapse = (root) => root.querySelectorAll('details[data-os-collapse]').forEach((d) => {
    d.open = mobile.matches ? d.hasAttribute('data-os-open') : true;
  });
  const initCollapse = (root) => {
    applyCollapse(root);
    if (alreadyClaimed(document.documentElement, 'osCollapseReady')) return;
    mobile.addEventListener('change', () => applyCollapse(document));
  };

  // Add-to-cart cards: the button adds one, then the stepper mirrors the cart
  // quantity for that variant. State is always taken from the cart's own reply,
  // so a rejected add (sold out, plan error) never shows a phantom quantity.
  const cardState = (card, qty) => {
    card.querySelector('[data-os-card-qty]').textContent = qty;
    card.querySelector('[data-os-card-stepper]').hidden = qty < 1;
    card.querySelector('[data-os-card-add]').hidden = qty >= 1;
  };
  const cardVariant = (card) => Number(card.dataset.variant);
  const quantityInCart = (cart, variantId) => cart.items.find((i) => i.variant_id === variantId)?.quantity || 0;
  // Cart changes made elsewhere (the drawer, another card) flow back into every
  // card on the page, unless that card has taps of its own still in flight.
  // One listener for the document; it walks the live cards each time.
  document.addEventListener('os:cart-updated', (e) => {
    document.querySelectorAll('[data-os-card][data-variant]').forEach((card) => {
      if (card.dataset.osPending === '1') return;
      cardState(card, quantityInCart(e.detail, cardVariant(card)));
    });
  });
  const initCards = (root) => {
    if (!window.OSCart) return; // os:cart-ready re-runs this once the cart script is up
    const cards = [...root.querySelectorAll('[data-os-card][data-variant]')]
      .filter((c) => !alreadyClaimed(c, 'osCardReady'));
    if (!cards.length) return;
    cards.forEach((card) => {
      const id = cardVariant(card);
      const qty = () => Number(card.querySelector('[data-os-card-qty]').textContent) || 0;
      const reconcile = (cart) => cardState(card, quantityInCart(cart, id));
      // A rejected request re-reads the cart, so the card never keeps a phantom quantity.
      const recover = (err) => {
        console.warn('[os-sections] cart update failed', err.message);
        return window.OSCart.getCart().then(reconcile);
      };
      card.querySelector('[data-os-card-add]')?.addEventListener('click', () => {
        // Paint the stepper at 1 immediately; the cart reply reconciles it and
        // the drawer opens only once Shopify has accepted the line.
        cardState(card, 1);
        window.OSCart.addVariant(id, 1)
          .then((cart) => { reconcile(cart); window.OSCart.open(); })
          .catch(recover);
      });
      // Steppers paint the new count at once; OSCart.coalesce sends one request
      // per burst of taps (the last value wins) and the reply reconciles the card.
      card.querySelectorAll('[data-os-card-step]').forEach((btn) => btn.addEventListener('click', () => {
        const next = Math.max(0, qty() + Number(btn.dataset.osCardStep));
        cardState(card, next);
        card.dataset.osPending = '1';
        window.OSCart.coalesce(`variant:${id}`, () => window.OSCart.changeVariant(id, next))
          .then(reconcile, recover)
          .finally(() => { card.dataset.osPending = '0'; });
      }));
    });
    window.OSCart.getCart().then((cart) => cards.forEach((card) => {
      const q = quantityInCart(cart, cardVariant(card));
      if (q) cardState(card, q);
    })).catch((err) => console.warn('[os-sections] cart read failed', err.message));
  };

  // Review videos: one plays at a time; the play button (or the video) toggles.
  const initVideos = (root) => root.querySelectorAll('[data-os-video]').forEach((box) => {
    const video = box.querySelector('video');
    if (!video || alreadyClaimed(box, 'osVideoReady')) return;
    const toggle = () => {
      if (!video.paused) { video.pause(); return; }
      document.querySelectorAll('[data-os-video] video').forEach((v) => { if (v !== video) v.pause(); });
      const p = video.play();
      if (p && p.catch) p.catch(() => { video.controls = true; }); // blocked: fall back to native controls
    };
    const btn = box.querySelector('[data-os-play]');
    if (btn) btn.addEventListener('click', toggle);
    const syncLabel = () => {
      if (btn) btn.setAttribute('aria-label', video.paused || video.ended || video.error ? 'Play video' : 'Pause video');
    };
    ['play', 'pause', 'ended', 'error', 'emptied'].forEach((event) => video.addEventListener(event, syncLabel));
    syncLabel();
    video.addEventListener('click', toggle);
    video.addEventListener('play', () => box.classList.add('is-playing'));
    ['pause', 'ended'].forEach((e) => video.addEventListener(e, () => box.classList.remove('is-playing')));
  });

  // Re-centre every snapping track when the viewport changes or a tab reveals one.
  const recentre = () => document.querySelectorAll('[data-os-scroller] [data-os-track]').forEach((t) => {
    if (t.offsetParent) centreFirst(t);
  });
  // Mobile browsers fire resize as their toolbars show and hide while scrolling;
  // only a real width change re-centres anything.
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    recentre();
  });
  document.addEventListener('os:tab-change', recentre);

  const init = (root = document) => {
    initTabs(root);
    initScrollers(root);
    initThumbs(root);
    initCollapse(root);
    initCards(root);
    initVideos(root);
  };
  document.addEventListener('DOMContentLoaded', () => init());
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('os:cart-ready', () => initCards(document));
})();
